import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import DualListBox from "../../../duallistBox";
import Cookies from "js-cookie";
import { useUserPermissions } from "../../../UserPermissionsContext";
import { Toast } from "react-bootstrap";

type User = {
  username: string | null;
  id: number;
  password: string | null;
  last_login: string | null;
  is_superuser: boolean | null;
  first_name: string | null;
  last_name: string;
  email: string | null;
  is_staff: boolean | null;
  is_active: boolean | null;
  date_joined: boolean | null;
  groups: Array<number>;
  user_permissions: Array<number>;
  bio?: string | null;
  phone_number?: string | null;
  states?: Array<number>;
  zones?: Array<number>;
  branches?: Array<number>;
};

type ErrorMessage = {
  username?: string[];
  password?: string[];
  is_active?: string[];
  first_name?: string[];
  last_name?: string[]
  email?: string[]
};

interface Group {
  id: number;
  name: string;
}

type Permission = { id: number; name: string };

const AddUser = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const jwtToken = Cookies.get("authToken");
  const { webApi, userPermissions } = useUserPermissions();
  const [errorMessage, setErrorMessage] = useState<ErrorMessage>({});
  const [groupsDropdown, setGroupsDropdown] = useState<Group[]>([]);
  const [chosenGroups, setChosenGroups] = useState<Group[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [chosenPermissions, setChosenPermissions] = useState<Permission[]>([]);
  const [showSolidDangerToast, setShowSolidDangerToast] = useState(false);
  const [showSolidSuccessToast, setshowSolidSuccessToast] = useState(false);
  const handleSolidSuccessToastClose = () => setshowSolidSuccessToast(false);
  const handleSolidDangerToastClose = () => setShowSolidDangerToast(false);
  const [toastMessage, setToastMessage] = useState("");
  useEffect(() => {
    if ((showSolidDangerToast || showSolidSuccessToast) === true) {
      setTimeout(() => setShowSolidDangerToast(false), 5000);
      setTimeout(() => setshowSolidSuccessToast(false), 5000);
    }
  }, [toastMessage])
  const [isLoading, setIsLoading] = useState(false);
  const checkPermission = (action: any, subject: any) => {
    return userPermissions.some(
      (ability) => ability.action === action && ability.subject === subject
    );
  };

  const [newUser, setNewUser] = useState<User>({
    user_permissions: [],
    username: null,
    id: 0,
    password: null,
    last_login: null,
    is_superuser: false,
    first_name: null,
    last_name: "",
    email: null,
    is_staff: null,
    is_active: null,
    date_joined: null,
    groups: [],
    bio: null,
    phone_number: null,
    states: [],
    zones: [],
    branches: [],
  });

  const options = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwtToken}`,
    },
  };

  const fetchGroupsData = async () => {
    const response = await fetch(
      `${webApi}/user/get_groups_dropdown/`,
      options
    );
    if (!response.ok) {
      const errorData = await response.json();
      if (errorData.code === "token_not_valid") {
        navigate("/academic/sri-chaitanya/crm/landing-page");
      }
      throw new Error(errorData.detail || "Failed to fetch user data");
    }
    return await response.json();
  };

  const fetchDefaultPermissions = async () => {
    const response = await fetch(
      `${webApi}/user/get_permissions_dropdown/group_id/0/`,
      options
    );
    return await response.json();
  };

  const fetchData = async () => {
    try {
      const [groupsData, permissionsData] = await Promise.all([
        fetchGroupsData(),
        fetchDefaultPermissions(),
      ]);
      setGroupsDropdown(groupsData.results);
      setPermissions(permissionsData.results);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
    }
  };

  useEffect(() => {
    if (!checkPermission("add_user", "user")) {
      navigate(routes.adminDashboard);
    } else {
      fetchData();
    }
  }, [userPermissions]);


  const updateData = async () => {
    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwtToken}`,
      },
      body: JSON.stringify(newUser),
    };
    const url = `${webApi}/user/users/`;
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      const errorData = await response.json();
      setErrorMessage(errorData);
      setToastMessage(errorData.username || errorData.password || errorData.is_active);
      if (toastMessage !== "") {
        setShowSolidDangerToast(true);
      }
      throw new Error("Network response was not ok");
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await updateData();
      navigate(routes.users);
    } catch (error) { }
    finally { setIsLoading(false); }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewUser({ ...newUser, [name]: value || null });
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewUser({ ...newUser, [name]: value === "true" });
  };

  const handlePermissionsChange = async (
    selected: Permission[] | Group[],
    type: "user_permissions" | "groups"
  ) => {
    if (type === "user_permissions") {
      setChosenPermissions(selected as Permission[]);
      setNewUser({
        ...newUser,
        user_permissions: (selected as Permission[]).map(
          (permission) => permission.id
        ),
      });
    } else if (type === "groups") {
      setChosenGroups(selected as Group[]);
      setNewUser({
        ...newUser,
        groups: (selected as Group[]).map((group) => group.id),
      });
      const groupIds =
        (selected as Group[]).map((group) => group.id).join(",") || "0";
      const response = await fetch(
        `${webApi}/user/get_permissions_dropdown/group_id/${groupIds}/`,
        options
      );
      const data = await response.json();
      setPermissions(data.results);
    }
  };

  return (
    <div>
      <div className="page-wrapper">
        <div className="content container-fluid">
          {/* Page Header */}
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <h3 className="page-title">Add User</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.users}>Users</Link>
                  </li>
                  <li className="breadcrumb-item active">Add Group</li>
                </ul>
              </div>
            </div>
          </div>
          {/* /Page Header */}

          {/* Row */}
          <div className="row justify-content-center">
            <div className="col-sm-12 d-flex justify-content-center">
              {/* Add Group */}
              <div
                className="card"
                style={{
                  width: "70%",
                  boxShadow: "0 4px 8px 0 rgba(0,0,0,0.2)",
                  borderRadius: "15px",
                }}
              >
                <div className="card-body">
                  <form
                    onSubmit={handleSubmit}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleSubmit(e);
                      }
                    }}
                  >
                    <div className="modal-body light-violet-bg">
                      <div className="row">
                        {/* First Name */}
                        <div className="col-12 col-md-6 mb-3">
                          <label className="form-label">First Name<span color="red">*</span></label>
                          <input
                            type="text"
                            className={`form-control ${errorMessage.first_name ? "is-invalid" : ""}`}
                            onChange={handleInputChange}
                            name="first_name"
                            placeholder="First Name"
                            value={newUser.first_name || ""}
                            required={true}
                          />
                          {errorMessage.first_name && (
                            <div className="invalid-feedback">{errorMessage.first_name[0]}</div>
                          )}
                        </div>

                        {/* Last Name */}
                        <div className="col-12 col-md-6 mb-3">
                          <label className="form-label">Last Name</label>
                          <input
                            type="text"
                            className={`form-control ${errorMessage.last_name ? "is-invalid" : ""}`}
                            onChange={handleInputChange}
                            name="last_name"
                            placeholder="Last Name"
                            value={newUser.last_name || ""}
                            // required
                          />
                          {errorMessage.last_name && (
                            <div className="invalid-feedback">{errorMessage.last_name[0]}</div>
                          )}
                        </div>

                        {/* Email */}
                        <div className="col-12 col-md-6 mb-3">
                          <label className="form-label">Email<span color="red">*</span></label>
                          <input
                            type="text"
                            className={`form-control ${errorMessage.email ? "is-invalid" : ""}`}
                            onChange={handleInputChange}
                            name="email"
                            placeholder="Email"
                            value={newUser.email || ""}
                            required
                          />
                          {errorMessage.email && (
                            <div className="invalid-feedback">{errorMessage.email[0]}</div>
                          )}
                        </div>

                        {/* User Name */}
                        <div className="col-12 col-md-6 mb-3">
                          <label className="form-label">User Name<span color="red">*</span></label>
                          <input
                            type="text"
                            className={`form-control ${errorMessage.username ? "is-invalid" : ""}`}
                            onChange={handleInputChange}
                            name="username"
                            placeholder="User Name"
                            value={newUser.username || ""}
                            required
                          />
                          {errorMessage.username && (
                            <div className="invalid-feedback">{errorMessage.username[0]}</div>
                          )}
                        </div>

                        {/* Password */}
                        <div className="col-12 col-md-6 mb-3">
                          <label className="form-label">Password<span color="red">*</span></label>
                          <input
                            type="password"
                            className={`form-control ${errorMessage.password ? "is-invalid" : ""}`}
                            onChange={handleInputChange}
                            name="password"
                            placeholder="Password"
                            value={newUser.password || ""}
                            required
                          />
                          {errorMessage.password && (
                            <div className="invalid-feedback">{errorMessage.password[0]}</div>
                          )}
                        </div>
                      </div>
                      <div className="radio-group d-flex justify-content-between">
                        <div
                          className="radio-group-item"
                          style={{
                            flex: 1,
                            marginRight: "35px",
                            marginBottom: "9px",
                            padding: "10px",
                            borderRadius: "5px",
                          }}
                        >
                          <label className="form-label">Is Superuser</label>
                          <div>
                            <input
                              type="radio"
                              id="is_superuser_yes"
                              name="is_superuser"
                              value="true"
                              onChange={handleRadioChange}
                            />
                            <label
                              htmlFor="is_superuser_yes"
                              style={{
                                marginLeft: "5px",
                                marginRight: "15px",
                                cursor: "pointer",
                              }}
                            >
                              Yes
                            </label>
                            <input
                              type="radio"
                              id="is_superuser_no"
                              name="is_superuser"
                              value="false"
                              onChange={handleRadioChange}
                            />
                            <label
                              htmlFor="is_superuser_no"
                              style={{
                                marginLeft: "5px",
                                cursor: "pointer",
                              }}
                            >
                              No
                            </label>
                          </div>
                        </div>
                        <div
                          className="radio-group-item"
                          style={{
                            padding: "10px",
                            borderRadius: "5px",
                          }}
                        >
                          <label className="form-label">Is Active<span color="red">*</span></label>
                          <div>
                            <input
                              type="radio"
                              id="is_active_yes"
                              name="is_active"
                              value="true"
                              onChange={handleRadioChange}
                              required={true}
                            />
                            <label
                              htmlFor="is_active_yes"
                              style={{
                                marginLeft: "5px",
                                marginRight: "15px",
                                cursor: "pointer",
                              }}
                            >
                              Yes
                            </label>
                            <input
                              type="radio"
                              id="is_active_no"
                              name="is_active"
                              value="false"
                              onChange={handleRadioChange}
                            />
                            <label
                              htmlFor="is_active_no"
                              style={{
                                marginLeft: "5px",
                                cursor: "pointer",
                              }}
                            >
                              No
                            </label>
                          </div>
                          {errorMessage.is_active && (
                            <div className="invalid-feedback">
                              {errorMessage.is_active[0]}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="form-group dual-list-box-container">
                        <label className="form-label">Groups</label>
                        <DualListBox
                          options={groupsDropdown}
                          selected={chosenGroups}
                          onChange={(selected) =>
                            handlePermissionsChange(selected, "groups")
                          }
                        />
                      </div>
                      <div className="form-group dual-list-box-container">
                        <label className="form-label">User Permissions</label>
                        <DualListBox
                          options={permissions}
                          selected={chosenPermissions}
                          onChange={(selected) =>
                            handlePermissionsChange(selected,"user_permissions")
                          }
                        />
                      </div>
                    </div>
                    <div className="modal-footer">
                      <Link
                        className="btn btn-primary"
                        type="submit"
                        to={routes.users}
                        style={{
                          backgroundColor: "lightcoral",
                          color: "white",
                          marginRight: "5px",
                        }}
                      >
                        Cancel
                      </Link>
                      <button className="btn btn-primary" type="submit" disabled={isLoading}>
                        {isLoading ? (
                          <i className="spinner-border spinner-border-sm"></i>
                        ) : (
                          <>Save</>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
              {/* /Add Group */}
            </div>
          </div>
          {/* /Row */}
          <Toast
            show={showSolidDangerToast}
            onClose={handleSolidDangerToastClose}
            id="solidDangerToast"
            className="colored-toast bg-danger text-fixed-white"
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            style={{
              position: "fixed",
              top: "20px",
              right: "20px",
              zIndex: 1050,
            }}
          >
            <Toast.Body>{toastMessage}</Toast.Body>
          </Toast>
          <Toast
            show={showSolidSuccessToast}
            onClose={handleSolidSuccessToastClose}
            id="solidSuccessToast"
            className="colored-toast bg-success text-fixed-white"
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            style={{
              position: "fixed",
              top: "20px",
              right: "20px",
              zIndex: 1050,
            }}
          >
            <Toast.Body>{toastMessage}</Toast.Body>
          </Toast>
        </div>
      </div>
    </div>
  );
};

export default AddUser;
